<template>
    <nav class="top-nav" role="navigation" aria-label="Main">
        <router-link :to="{name: 'dashboard'}" class="brand">
            <span>my</span>247ed.com
        </router-link>

        <!-- Primary sections -->
        <ul class="primary is-hidden-touch">
            <li v-for="section in sections" :key="section.key" :class="{'active': section.key === activeKey}">
                <router-link :to="section.route">
                    <i class="fad fa-fw" :class="section.icon"></i>
                    <span class="nav-label" :class="{'has-short': section.short}">{{ section.label }}</span>
                    <span class="nav-label nav-short" v-if="section.short">{{ section.short }}</span>
                    <i v-if="sectionFlags[section.key]" class="fas fa-circle flag" :class="sectionFlags[section.key]"></i>
                </router-link>
            </li>
        </ul>

        <!-- Utility: account, support, logout -->
        <div class="utility is-hidden-touch">
            <router-link v-if="currentUser" :to="{name: 'account'}" class="user" :title="currentUser.first_name + ' ' + currentUser.last_name">
                <i class="fad fa-fw fa-user-circle"></i>
                <span class="nav-label">{{ currentUser.first_name }} {{ currentUser.last_name }}</span>
            </router-link>
            <router-link v-if="currentUser" :to="{name: 'account'}" class="is-light" :class="{'active': activeKey === 'account'}" title="Settings">
                <i class="fad fa-fw fa-gear"></i>
                <span class="nav-label">Settings</span>
            </router-link>
            <router-link :to="{name: 'support'}" class="is-light" :class="{'active': activeKey === 'support'}" title="Support">
                <i class="fad fa-fw fa-life-ring"></i>
                <span class="nav-label">Support</span>
            </router-link>
            <a @click="logout" class="is-light" title="Logout">
                <i class="fad fa-fw fa-sign-out-alt"></i>
                <span class="nav-label">Logout</span>
            </a>
        </div>

        <!-- Touch: everything behind one menu -->
        <div class="mobile is-hidden-desktop">
            <b-dropdown aria-role="menu" position="is-bottom-left">
                <template #trigger>
                    <a class="burger-trigger"><i class="fas fa-bars"></i></a>
                </template>

                <b-dropdown-item v-for="section in sections" :key="section.key" has-link aria-role="menuitem">
                    <router-link :to="section.route" :class="{'has-text-weight-bold': section.key === activeKey}">
                        <i class="fad fa-fw ml-1 mr-2 has-text-primary" :class="section.icon"></i>{{ section.label }}
                        <i v-if="sectionFlags[section.key]" class="fas fa-circle flag ml-2" :class="sectionFlags[section.key]"></i>
                    </router-link>
                </b-dropdown-item>
                <hr class="dropdown-divider">
                <b-dropdown-item has-link aria-role="menuitem" v-if="currentUser">
                    <router-link :to="{name: 'account'}"><i class="fad fa-fw fa-user-circle ml-1 mr-2 has-text-primary"></i>Your Account</router-link>
                </b-dropdown-item>
                <b-dropdown-item has-link aria-role="menuitem">
                    <router-link :to="{name: 'support'}"><i class="fad fa-fw fa-life-ring ml-1 mr-2 has-text-primary"></i>Support</router-link>
                </b-dropdown-item>
                <b-dropdown-item aria-role="menuitem" @click="logout">
                    <i class="fad fa-fw fa-sign-out-alt ml-1 mr-2 has-text-primary"></i>Logout
                </b-dropdown-item>
            </b-dropdown>
        </div>
    </nav>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";
    import {SECTIONS, routeSectionKey} from "../../constants/navigation";
    import navFlags from "./navFlags";

    export default {
        name: "TopNav",
        mixins: [navFlags],
        computed: {
            ...mapGetters(['currentUser', 'certificationOn', 'certificationsLoaded']),
            // Sections tied to a certification (Professional Development →
            // clock hours) only appear once the switches are loaded and that
            // one is on — never on the pre-load assumption
            sections() {
                return SECTIONS.filter(s => !s.certification || (this.certificationsLoaded && this.certificationOn(s.certification)));
            },
            activeKey() {
                return routeSectionKey(this.$route);
            }
        },
        methods: {
            ...mapActions(['signOut']),
            logout() {
                this.signOut();
                this.$router.push({name: 'login'});
            }
        },
        mounted() {
            // The bar is on every signed-in page: make sure the switches are
            // loaded even when App.vue's boot loads didn't run (fresh login)
            if (this.currentUser && !this.certificationsLoaded) this.$store.dispatch('getCertifications').catch(() => {});
        }
    };
</script>

<style lang="scss" scoped>
    $nav-height: 68px;
    $nav-blue: #336699;
    $nav-text: #4a4a4a;

    .top-nav {
        position: relative;
        z-index: 130; // above the floating side panel (120) so the touch menu opens over it
        display: flex;
        align-items: stretch;
        height: $nav-height;
        min-height: $nav-height;
        padding: 0 1.25rem 0 1.5rem;
        background: #fff;
        border-bottom: 1px solid #dfe2e8;

        @media (max-width: 768px) {
            padding: 0 0.75rem 0 1rem;
        }
    }

    .brand {
        display: flex;
        align-items: center;
        margin-right: 2rem;
        font-size: 26px;
        font-weight: 400;
        letter-spacing: -1px;
        color: $nav-text;
        white-space: nowrap;

        span {
            font-weight: 900;
            margin-right: 3px;
            color: $nav-blue;
        }

        @media (max-width: 768px) {
            font-size: 20px;
            margin-right: 1rem;
        }
    }

    .primary {
        display: flex;
        align-items: stretch;
        flex: 1;
        list-style: none;
        margin: 0;
        padding: 0;

        li {
            display: flex;
            align-items: center;
            margin-right: 0.35rem;

            a {
                position: relative;
                display: flex;
                align-items: center;
                padding: 0.5rem 1.1rem;
                border-radius: 6px;
                color: $nav-text;
                font-weight: 600;
                font-size: 0.95rem;
                white-space: nowrap;
                transition: background-color 150ms, color 150ms;

                i {
                    color: #3c6691;
                    font-size: 1.15rem;
                    margin-right: 0.55rem;
                    transition: color 150ms;
                }

                &:hover {
                    background-color: #567ea7;
                    color: white;

                    i {
                        color: white;
                    }
                }
            }

            // Active section: a blue rounded-rect badge with white text, echoing the active
            // item in the left submenu so the two levels read as one system
            &.active a {
                background-color: $nav-blue;
                color: white;

                i {
                    color: white;
                }

                .flag {
                    color: white !important;
                }
            }
        }
    }

    .flag {
        font-size: 0.5rem !important;
        margin-left: 0.45rem;
        margin-right: 0 !important;
        vertical-align: middle;
    }

    .utility {
        display: flex;
        align-items: center;
        margin-left: auto;
        gap: 0.35rem;

        a {
            display: flex;
            align-items: center;
            padding: 0.45rem 0.6rem;
            border-radius: 4px;
            color: $nav-text;
            font-weight: 600;
            font-size: 0.9rem;
            white-space: nowrap;
            transition: background-color 150ms, color 150ms;

            i {
                color: #3c6691;
                font-size: 1.15rem;
            }

            .nav-label {
                margin-left: 0.45rem;
                color: inherit;
                font-weight: inherit;
                font-size: inherit;
            }

            &.is-light {
                font-weight: 400;
            }

            &:hover {
                background-color: #567ea7;
                color: white;

                i {
                    color: white;
                }
            }

            // Same blue badge as the active primary section
            &.active {
                background-color: $nav-blue;
                color: white;

                i {
                    color: white;
                }
            }
        }

        // Collapse to icons early so the primary sections keep their labels;
        // the title attribute still names each on hover. The user link goes
        // entirely (the gear already leads to the same place).
        @media (max-width: 1520px) {
            a .nav-label {
                display: none;
            }

            .user {
                display: none;
            }
        }
    }

    // The short label stands in for the long one on narrow desktop widths
    .primary .nav-short {
        display: none;
    }

    @media (max-width: 1400px) {
        .primary .nav-label.has-short {
            display: none;
        }

        .primary .nav-short {
            display: inline;
        }
    }

    // Narrow desktop: tighten the section links so the bar fits without
    // clipping the utility icons
    @media (max-width: 1240px) {
        .brand {
            margin-right: 1rem;
        }

        .primary li a {
            padding: 0.5rem 0.75rem;
            font-size: 0.9rem;
        }

        .utility a {
            padding: 0.45rem 0.45rem;
        }
    }

    .mobile {
        display: flex;
        align-items: center;
        margin-left: auto;

        .burger-trigger {
            padding: 0.4rem 0.6rem;
            color: $nav-text;
            font-size: 1.3rem;
        }
    }
</style>
